import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';
import type { ProposalOption } from '@/types/proposal';

export interface PortalProposalListItem {
  id: string;
  proposalNumber: string;
  status: string;
  total: number;
  /** Date-only calendar value (validUntil) — render with a date-only formatter. */
  validUntil: string | null;
}

/** The customer-safe proposal detail (the shared public field set, projected). */
export interface PortalProposalDetail {
  redirectUrl?: string | null;
  customerAcceptedAt?: string | null;
  id: string;
  proposalNumber: string;
  status: string;
  options: ProposalOption[];
  selectedOptionIndex?: number | null;
  validUntil?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  selectionPreviewSupported?: boolean;
  notes?: string | null;
  terms?: string | null;
  customer?: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
  } | null;
  attachments?: import('@/types/proposal').ProposalOptionAttachment[];
  company?: {
    name?: string | null;
    logoUrl?: string | null;
    logoDataUri?: string | null;
  } | null;
  timezone?: string | null;
  /** Resolved design layout — single-scope when `branding.presentation` says so. */
  resolvedDocLayout?: import('@/types/estimateDocTemplate').EstimateDocLayout | null;
  /** Whether approving must carry a drawn signature. */
  signatureRequired?: boolean;
  customerSignatureName?: string | null;
  customerSignatureUrl?: string | null;
  customerSignatureAt?: string | null;
}

/** A drawn signature — typed name plus the pad's PNG data URL. */
export interface PortalProposalSignature {
  name: string;
  image: string;
}

/** The signed-in contact's proposals (newest first). */
export function usePortalProposals() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'proposals', token],
    queryFn: () => portalGet<PortalProposalListItem[]>('/api/portal/proposals'),
    enabled: Boolean(token),
    retry: false,
  });
}

/** One proposal the contact owns; 404s (via ApiError) for anything else. */
export function usePortalProposal(id: string) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'proposal', id, token],
    queryFn: () =>
      portalGet<PortalProposalDetail>(`/api/portal/proposals/${encodeURIComponent(id)}`),
    enabled: Boolean(token && id),
    retry: false,
  });
}

function invalidateProposal(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  void queryClient.invalidateQueries({ queryKey: ['portal', 'proposal', id] });
  void queryClient.invalidateQueries({ queryKey: ['portal', 'proposals'] });
}

/** Accept an option (→ RESOLVED). `terms_accepted` must be true. Materializes an
 * estimate server-side; no card charge here. */
export function usePortalSelectOption(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      optionIndex,
      signature,
      acceptMembershipOffer,
      membershipBillingOptionId,
      lineItems,
      expectedTotal,
      depositFirst = false,
      checkoutFirst = false,
    }: {
      optionIndex: number;
      /** The confirmed total the customer saw; rejected if server repricing differs. */
      expectedTotal?: number;
      /** Choose only; required payment precedes consent and signature in checkout. */
      depositFirst?: boolean;
      /** Choose only; review the configured plan before consent and signature. */
      checkoutFirst?: boolean;
      signature?: PortalProposalSignature | null;
      /** The join-&-save membership offer the customer ticked for this option. */
      acceptMembershipOffer?: boolean;
      membershipBillingOptionId?: string | null;
      /** The customer's optional add-on picks — the server re-prices from them. */
      lineItems?: Array<{ lineItemId: string; lineKey?: string; isSelected: boolean }>;
    }) =>
      portalPost<PortalProposalDetail>(
        `/api/portal/proposals/${encodeURIComponent(id)}/${depositFirst || checkoutFirst ? 'choose-option-for-deposit' : 'select-option'}`,
        {
          option_index: optionIndex,
          ...(expectedTotal != null ? { expectedTotal } : {}),
          ...(!depositFirst && !checkoutFirst ? { terms_accepted: true } : {}),
          ...(!depositFirst && !checkoutFirst && signature
            ? {
                customer_signature_name: signature.name,
                customer_signature_image: signature.image,
              }
            : {}),
          ...(acceptMembershipOffer ? { acceptMembershipOffer: true } : {}),
          ...(acceptMembershipOffer && membershipBillingOptionId
            ? { membershipBillingOptionId }
            : {}),
          ...(lineItems && lineItems.length ? { lineItems } : {}),
        },
      ),
    onSuccess: (updated) => {
      queryClient.setQueriesData({ queryKey: ['portal', 'proposal', id] }, updated);
      invalidateProposal(queryClient, id);
    },
  });
}

/** Decline the proposal (→ DECLINED). */
export function usePortalDeclineProposal(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      portalPost<PortalProposalDetail>(`/api/portal/proposals/${encodeURIComponent(id)}/decline`, {
        reason,
      }),
    onSuccess: () => invalidateProposal(queryClient, id),
  });
}
